import * as THREE from 'three';
import * as config from '../config.json';
import { RewardProgramsDiscConfig } from '../config';
import CruciblePiece from './cruciblePiece';
import CrucibleData from 'helpers/nft/crucibleData';
import {
  featuredProgramsAssets,
  FeaturedRewardProgram
} from '../featuredRewardPrograms';
import CrucibleLoader from '../crucibleLoader';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';

export class RewardProgramsDisc extends CruciblePiece {
  protected _config: RewardProgramsDiscConfig;
  protected _coinNodes: { [key: string]: THREE.Object3D };
  protected _logos: THREE.Mesh[];
  protected _userPrograms: {
    [key: string]: {
      assets: FeaturedRewardProgram;
      multiplier: number;
    };
  };

  constructor() {
    super();
    this._config = config.rewardProgramsDisc;
    this._coinNodes = {};
    this._logos = [];
    this._userPrograms = {};
  }

  setup(gltf: GLTF) {
    // Clean crucible piece objects objects
    this._coinNodes = {};
    this._logos = [];
    this._userPrograms = {};

    this.group = gltf.scene;
    this.group.scale.set(0.02, 0.02, 0.02); // This should be removed with final models

    this.group.traverse((node: any) => {
      if (node.isMesh) {
        node.castShadow = true;
        node.receiveShadow = true;
        if (node.material.map) node.material.map.anisotropy = 16;
      }
    });
  }

  update(
    color: THREE.Color,
    loader: CrucibleLoader,
    crucibleData: CrucibleData,
    coinStyle: string = ''
  ) {
    this._coinNodes = {};
    this._logos = [];
    this._userPrograms = {};

    crucibleData.historicalRewardPrograms.forEach((rewardProgram) => {
      const maintainer = rewardProgram.maintainer;
      const featuredProgramData =
        featuredProgramsAssets[rewardProgram.maintainer];

      // if the program hasn't been added to the user's programs yet
      if (!this._userPrograms[maintainer]) {
        this._userPrograms[maintainer] = {
          assets: featuredProgramData,
          multiplier: rewardProgram.multiplierProgress
        };
      }
      // if there's more than one historical program of the same maintainer
      else if (
        this._userPrograms[maintainer].multiplier < rewardProgram.multiplier
      ) {
        this._userPrograms[maintainer].multiplier = rewardProgram.multiplier;
      }
    });

    // for each program saved
    Object.keys(this._userPrograms).forEach((maintainer) => {
      // check if there's a special coin style and change model url
      let coinModelUrl = this._userPrograms[maintainer].assets.coinModelUrl;
      if (coinStyle !== '')
        coinModelUrl = coinModelUrl.replace('coin', `coin_${coinStyle}`);

      this._createLogoMesh(
        color,
        loader,
        this._userPrograms[maintainer].assets.logoUrl,
        coinModelUrl,
        maintainer,
        this._userPrograms[maintainer].multiplier
      );
    });
  }

  animate(timeElapsed: number) {
    this._logos.forEach((logo) => {
      logo.rotation.y += this._config.logoRotationSpeed * timeElapsed;
    });
  }

  async _createLogoMesh(
    color: THREE.Color,
    loader: CrucibleLoader,
    logoUrl: string,
    coinModelUrl: string,
    maintainer: string,
    multiplier: number
  ) {
    // Load coinModel for maintainer
    const coinGltf = await loader.loadModel(coinModelUrl);

    if (coinGltf === undefined) {
      return;
    }

    coinGltf.scene.name = 'Coin ' + maintainer;
    const coinModel = coinGltf.scene;

    let pivot = new THREE.Object3D();
    let coinMeshPivot = new THREE.Object3D();

    coinModel.traverse((node) => {
      if (node.name === this._config.pivotNode) pivot = node;
      if (node.name === this._config.coinPivot) coinMeshPivot = node;
    });

    // Load texture for maintainer logo
    const logoTexture = await loader.loadTexture(logoUrl, false, true);

    const logoPlaneGeometry = new THREE.PlaneGeometry(75, 75);
    const logoPlaneMaterial = new THREE.MeshPhongMaterial({
      map: logoTexture,
      transparent: true,
      opacity: 1 * multiplier,
      color: color,
      emissive: color,
      emissiveIntensity: 2.5 * multiplier,
      side: THREE.DoubleSide
    });

    const logoPlaneMesh = new THREE.Mesh(logoPlaneGeometry, logoPlaneMaterial);

    // Create group to add all coins
    const coinGroup = new THREE.Group();

    coinGroup.add(coinModel);
    coinGroup.name = maintainer;

    this.group.add(coinGroup);

    // Duplicate meshes based on multiplier
    const coinCountRounded = Math.floor(multiplier * 10);
    const coinCount = Math.max(coinCountRounded, 1);

    for (let i = 1; i < coinCount; i++) {
      const coinClone = coinMeshPivot.clone(true);
      pivot.add(coinClone);
      coinClone.position.y += 10 * i;
    }

    // Save coin meshes in array
    this._coinNodes[maintainer] = coinGroup;

    // Add Logo as child of Coin and position it
    coinMeshPivot.add(logoPlaneMesh);
    logoPlaneMesh.scale.set(1, 1, 1);
    const yPos = 50 + 10 * coinCount;
    logoPlaneMesh.position.set(0, yPos, 0);

    // Save logos in array to be animated later
    this._logos.push(logoPlaneMesh);

    // Rotate whole object
    const rotateCount = Object.keys(this._coinNodes).length - 1;
    if (rotateCount > 0) {
      pivot.rotation.y += (Math.PI / 4) * rotateCount;
    }
  }
}
